import React, { useEffect, useState } from 'react'

import { ConfigValueItem } from 'constants/Constants'
import ColumnLabel from 'components/headers/table/ColumnLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faPencil, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import _ from 'lodash'
import { ConfirmDeleteModal } from 'components/modals/ConfirmDeleteModal'
import InputField from 'components/inputs/InputField'
import { FormInputError } from 'IndexProductScreen'
import { SearchBar } from 'components/inputs/SearchBar'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { BaseModal } from 'components/modals/BaseModal'
import { usePipeMaterials } from 'hooks/usePipeMaterials'
import { toastError, toastSuccess } from 'services/Toast'

{/* TODO: Can be pulled out into a common table header component */}
const ConfigTableHeaders = () => {

    return (
        <div className="columns is-vcentered" style={{ borderBottom: '1px solid rgb(228, 228, 228)', backgroundColor: '#fafafa' }}>
            <div className="column is-5">   
                <ColumnLabel label={`Label`} />
            </div>
            <div className="column is-5">
                <ColumnLabel label={`Value`} />
            </div>
            <div className="column is-1">
                {/* Leave space for actions button. */}
            </div>
        </div>
    )
}

type ConfigItemFormState = {
    label: string,
    value: string,
    shortDescription?: string,
}

const initialFormState: ConfigItemFormState = {
    label: '',
    value: ''
}

const initialFormInputErrorState: FormInputError = {
    isError: false,
    errorMessage: null
}

interface FormErrorState {
    label: FormInputError,
    value: FormInputError,
    shortDescription: FormInputError,
}

const initialFormErrorState: FormErrorState = {
    label: initialFormInputErrorState,
    value: initialFormInputErrorState,
    shortDescription: initialFormInputErrorState,
}

const ManagePipeMaterialsScreen = ({

}:{


}) => {

    const [itemToDelete, setItemToDelete] = useState<ConfigValueItem>(null)
    const [isInEditState, setIsInEditState] = useState<boolean>(false)
    const [isConfirmDeleteModalActive, setIsConfirmDeleteModalActive] = useState<boolean>(false)
    const [filterablePipeMaterials, setFilterablePipeMaterials] = useState<ConfigValueItem[]>(null)
    const [isPipeMaterialFormModalActive, setIsPipeMaterialFormModalActive] = useState<boolean>(false)
    const [configItemFormState, setConfigItemFormState] = useState<ConfigItemFormState>(initialFormState)
    const [formErrorState, setFormErrorState] = useState<FormErrorState>(initialFormErrorState)

    const { pipeMaterials, deletePipeMaterial, isDeleting, deleteError, addPipeMaterial, isAdding, addError, editPipeMaterial, isEditing, editError } = usePipeMaterials()
    
    // Filter out deleted items when pipeMaterials changes
    useEffect(() => {
        if (pipeMaterials) {
            const nonDeletedPipeMaterials = pipeMaterials
                .filter(item => !item.isDeleted)
            
            setFilterablePipeMaterials(nonDeletedPipeMaterials)
        }
    }, [pipeMaterials])

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {

        setConfigItemFormState({
            ...configItemFormState,
            [e.target.name]: e.target.value
        })
    }

    const onCancelConfirmDeleteModal = () => {
        setIsConfirmDeleteModalActive(false)
        setItemToDelete(null)
    }

    const onDeletePipeMaterialItem = async (e: React.MouseEvent, itemToDelete: ConfigValueItem) => {
        e.preventDefault(); 
        setItemToDelete(itemToDelete)
        setIsConfirmDeleteModalActive(true) 
    }

    const onEditPipeMaterialItem = async (e: React.MouseEvent, itemToEdit: ConfigValueItem) => {
        e.preventDefault(); 

        setIsInEditState(true)

        setConfigItemFormState(itemToEdit)

        setIsPipeMaterialFormModalActive(true) 
    }

    const onChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (pipeMaterials) {
            const nonDeletedPipeMaterials = pipeMaterials.filter(item => !item.isDeleted)
            setFilterablePipeMaterials(nonDeletedPipeMaterials.filter(item => 
                item.value.toLowerCase().split("_").join(" ").startsWith(e.target.value.toLowerCase())
            ))
        }
    }
    
    const onClearSearchQuery = (e: React.MouseEvent) => {
        if (pipeMaterials) {
            const nonDeletedPipeMaterials = pipeMaterials
                .filter(item => !item.isDeleted)
            setFilterablePipeMaterials(nonDeletedPipeMaterials)
        }
    }
   
    const onCancelModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setConfigItemFormState(initialFormState)
        setFormErrorState(initialFormErrorState)
        setIsPipeMaterialFormModalActive(false)
        setIsInEditState(false)
    }

    const deletePipeMaterialHandler = async () => {
        try {
            await deletePipeMaterial(itemToDelete)
            
            toastSuccess(`Success, pipe material deleted.`)
            onCancelConfirmDeleteModal()
            
        } catch (error) {
            toastError()
        }
    }

    const validateForm = (): boolean => {

        const mustNotBeEmptyError: string = `Must not be empty`

        const formErrors: FormErrorState = Object.entries(configItemFormState).reduce((acc, [key, value]) => {

            if (key === 'shortDescription') {
                return {
                    ...acc,
                    [key]: {
                        isError: false,
                        errorMessage: null,
                    }
                }
            }
            
            return {
                ...acc,
                [key]: {
                    isError: value === null || value === '',
                    errorMessage: value === null || value === '' ? mustNotBeEmptyError : null,
                }
            }
        }, initialFormErrorState)

        setFormErrorState(formErrors)

        return Object.entries(formErrors).every(([key, value])=> {
            const formError: FormInputError = value
            return formError.isError === false
        })
    }


    const onSubmitForm = async (e: React.MouseEvent) => {
        e.preventDefault()

        const isFormValid: boolean = validateForm()

        if (isFormValid) {
            try {
                if (isInEditState) {
                    await editPipeMaterial(configItemFormState)
                    toastSuccess(`Success, pipe material updated.`)
                } else {
                    await addPipeMaterial(configItemFormState)
                    toastSuccess(`Success, pipe material added.`)
                }

                onCancelModal(e)
            } catch (error) {
                toastError()
            }
        }
    }

    return(
        <div>  
            <BaseModal
                isModalActive={isPipeMaterialFormModalActive}
                modalTitle={`${isInEditState ? `Edit ` : `Add New `} Pipe Material`}
                onCancelModal={onCancelModal}>
                    <form>

                        <InputField
                            fieldLabel={`Value`}
                            onChange={onChangeInput}
                            fieldName={'value'}
                            value={configItemFormState.value}
                            errorValue={formErrorState.value}
                            isDisabled={isInEditState}
                            helpText={``} />  

                        <InputField
                            fieldLabel={`Label`}
                            onChange={onChangeInput}
                            fieldName={'label'}
                            value={configItemFormState.label}
                            errorValue={formErrorState.label}
                            helpText={``} />                                         

                        <div className="field is-grouped is-pulled-right pt-4">
                            <div className="control">
                                <button className={`button is-link ${isInEditState ? (isEditing ? `is-loading` : ``) : (isAdding ? `is-loading` : ``)}`} onClick={onSubmitForm}>{isInEditState ? `Edit` : `Add`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={onCancelModal}>Cancel</button>
                            </div>
                        </div>

                    </form>
            </BaseModal>

        <ConfirmDeleteModal 
            isModalActive={isConfirmDeleteModalActive}
            isLoading={isDeleting}
            onCancel={onCancelConfirmDeleteModal}
            onConfirmDelete={deletePipeMaterialHandler} />

        <div className="columns mb-6">
            <div className="column is-10">
                    <h1 className="title is-3">Manage Pipe Materials</h1>
                    <h1 className="subtitle is-5 mb-3"></h1>
            </div>
            <div className="column">
                <button className="button is-link is-pulled-right" onClick={() => setIsPipeMaterialFormModalActive(true)}>Add <FontAwesomeIcon className="pl-2" icon={faPlus} /></button>
            </div>
        </div>

        {pipeMaterials && pipeMaterials.length > 0 
        ?
        <>
        <div className="columns mb-4 pb-4">
            <div className="column is-5 p-0">
                <SearchBar 
                    onChange={onChangeSearchQuery}
                    onClear={onClearSearchQuery} />
            </div>
        </div>

        {filterablePipeMaterials && filterablePipeMaterials.length > 0
        ?
        <div className="columns">
            <div className="column is-12">
                <div className="block">

                    <ConfigTableHeaders />

                    {filterablePipeMaterials.map((item, idx) => (
                        <div className={`columns is-vcentered pb-1 pt-1 ${item.isDeleted ? `has-background-white-bis` : ``}`} key={`code-${idx}`} style={{borderBottom: '#e4e4e4 1px solid'}}>
                            <div className="column is-5">
                                {item.label}
                            </div>
                            <div className="column is-5">
                                {item.value}
                            </div>       
                            <div className="column is-narrow">
                                {item.isDeleted
                                    ?
                                    <span className="tag is-danger is-light">Deleted</span>
                                    :
                                    <div className="dropdown is-hoverable is-pulled-right">
                                        <div className="dropdown-trigger pl-3 pr-3 is-clickable">
                                            <FontAwesomeIcon aria-haspopup="true" aria-controls="dropdown-menu4" icon={faEllipsisVertical} size={`lg`} />
                                        </div>
                                        <div className="dropdown-menu" id="dropdown-menu4" role="menu">
                                            <div className="dropdown-content">
                                                <a href="#" className="dropdown-item" onClick={(e) => onDeletePipeMaterialItem(e, item)}>
                                                    <span><FontAwesomeIcon icon={faTrash} className="pr-2" /> Delete</span>
                                                </a>
                                                <hr className="navbar-divider"/>
                                                <a href="#" className="dropdown-item" onClick={(e) => onEditPipeMaterialItem(e, item)}>
                                                    <span><FontAwesomeIcon icon={faPencil} className="pr-2" /> Edit</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    ))}
                    
                </div>
            </div>
        </div>
        :
        <div className="has-text-centered">
            <p>No pipe material(s) found.</p>
        </div>
        }
        </>
        :
        <PageLoadSpinner />
    }       
    </div>
    )
}

export { ManagePipeMaterialsScreen }